import { useCallback, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { billingService } from "../services/billing.service";
import { tagOf } from "../utils/api-error";
import { formatAmount } from "../utils/money";
import {
  Plan,
  PendingPlanChange,
  SubscriptionState,
} from "../interfaces/billing.interface";
import { useAuth } from "../context/AuthContext";
import AddonStepper from "../components/AddonStepper";

type Interval = "month" | "year";

// Tags the billing endpoints this page can hit document (checkout-session,
// upgrade, downgrade — docs/web-api-spec.md). Anything else is a server-side
// surprise and gets the one generic message.
const KNOWN_TAGS = [
  "unknown_plan",
  "already_subscribed",
  "stripe_unavailable",
  "unknown_user",
  "not_an_upgrade",
  "not_a_downgrade",
  "card_declined",
  "authentication_failed",
  "no_subscription",
  "school_managed",
  "addon_quantity_invalid",
  "addon_price_missing",
];

const Pricing = () => {
  const { t, i18n } = useTranslation(["billing", "common"]);
  // Falls back the same way the server does (docs/web-api-spec.md
  // §/billing/plans): anything but a resolved "en"/"zh" resolves to "en".
  const lang = i18n.resolvedLanguage ?? "en";
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [sub, setSub] = useState<SubscriptionState | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingPlanChange | null>(
    null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [errorTag, setErrorTag] = useState<string | null>(null);
  // Yearly leads (it's the better-value default); clamped in render to an
  // interval the API actually returns.
  const [selected, setSelected] = useState<Interval>("year");
  // Extra subjects chosen per plan card; absent = 0. Only checkout cards show
  // the picker — an existing subscriber changes add-ons on /account.
  const [addons, setAddons] = useState<Record<string, number>>({});

  const loadPlans = useCallback(async () => {
    setLoadFailed(false);
    try {
      setPlans(await billingService.getPlans(lang));
    } catch {
      setLoadFailed(true);
    }
  }, [lang]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  // The current subscription personalises each card's CTA (Current / Upgrade /
  // Downgrade). Only for a signed-in user; a failure just falls the CTAs back
  // to "Choose plan" (checkout), never blocks the page.
  const loadSub = useCallback(async () => {
    if (!isAuthenticated) {
      setSub(null);
      setPendingChange(null);
      return;
    }
    try {
      const s = await billingService.getSubscription();
      setSub(s ?? null);
      // A scheduled change only exists for a self-paid, non-free subscriber;
      // skip the round-trip otherwise. A failure just leaves the card's CTA
      // as-is (no "Scheduled" marker), never blocks the page.
      if (s && s.source === "self" && s.tier !== "free") {
        setPendingChange(
          await billingService.getPendingChange().catch(() => null),
        );
      } else {
        setPendingChange(null);
      }
    } catch {
      setSub(null);
      setPendingChange(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadSub();
  }, [loadSub]);

  // A message raised in one language must not survive a language switch and
  // re-render, stale, as if it had just happened.
  useEffect(() => {
    setErrorTag(null);
  }, [lang]);

  const onChoosePlan = async (planCode: string) => {
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }
    setErrorTag(null);
    setPendingCode(planCode);
    try {
      const { url } = await billingService.createCheckoutSession(
        planCode,
        addons[planCode] ?? 0,
      );
      // A full navigation, not react-router's navigate() — the destination
      // is Stripe's domain, not a route in this app.
      window.location.assign(url);
    } catch (err) {
      setErrorTag(tagOf(err) ?? "generic");
    } finally {
      setPendingCode(null);
    }
  };

  if (loadFailed) {
    return (
      <div
        data-testid="page-pricing"
        className="mx-auto max-w-md px-4 py-12 text-center"
      >
        <p role="alert" className="mb-4">
          {t("pricing.loadError")}
        </p>
        <button type="button" onClick={() => void loadPlans()} className="btn">
          {t("common:actions.retry")}
        </button>
      </div>
    );
  }

  if (!plans) {
    return (
      <div
        data-testid="page-pricing"
        className="mx-auto max-w-md px-4 py-12 text-center"
      >
        {t("common:actions.loading")}
      </div>
    );
  }

  // D3: a school provides this student's subjects — no personal pricing. The
  // server returns 403 school_managed on checkout regardless.
  // ponytail: plans can flash briefly before the subscription read lands;
  // gate the render on it if that ever shows.
  if (sub?.schoolManaged) {
    return (
      <div
        data-testid="page-pricing"
        className="mx-auto max-w-md px-4 py-12 text-center"
      >
        <p data-testid="school-managed">{t("pricing.schoolManaged")}</p>
      </div>
    );
  }

  // Only the intervals the API actually returns get a segment; `active`
  // falls back to the first present one if the yearly default is missing.
  const intervalsPresent = (["month", "year"] as Interval[]).filter((iv) =>
    plans.some((plan) => plan.interval === iv),
  );
  const active = intervalsPresent.includes(selected)
    ? selected
    : intervalsPresent[0];
  // The free tier is not a billing interval — it is the baseline both segments
  // are measured against, so it shows under monthly and yearly alike. Its
  // sortOrder of 0 keeps it first in either.
  const visiblePlans = plans.filter(
    (plan) => plan.tier === "free" || plan.interval === active,
  );
  // The plan the user is on now — matched by tier and interval — but only when
  // it is their own paid subscription. A free tier or a parent-paid entitlement
  // has nothing to upgrade/downgrade FROM, so every card stays "Choose plan".
  const currentPlan =
    sub && sub.source === "self" && sub.tier !== "free"
      ? plans.find(
          (plan) => plan.tier === sub.tier && plan.interval === sub.interval,
        ) ?? null
      : null;
  // Biggest annual saving on offer — the nudge toward the yearly segment.
  const yearSaving = Math.max(
    0,
    ...plans
      .filter((plan) => plan.interval === "year")
      .map((plan) => plan.annualSavingPercent ?? 0),
  );

  return (
    <div data-testid="page-pricing" className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="mb-2 font-display text-3xl font-black sm:text-4xl">
        {t("pricing.title")}
      </h1>
      <p className="mb-8 text-ink-soft">{t("pricing.subtitle")}</p>

      {intervalsPresent.length > 1 && (
        <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
          <div className="relative inline-flex rounded-full border border-hairline bg-surface p-1 shadow-soft">
            {intervalsPresent.map((iv) => (
              <button
                key={iv}
                type="button"
                data-testid={`interval-${iv}`}
                aria-pressed={active === iv}
                onClick={() => setSelected(iv)}
                className="relative rounded-full px-5 py-2 font-display text-sm font-extrabold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apricot"
              >
                {active === iv && (
                  <motion.span
                    layoutId="interval-pill"
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full bg-apricot"
                    transition={
                      reduce
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 400, damping: 32 }
                    }
                  />
                )}
                <span
                  className={`relative z-10 transition-colors ${
                    active === iv ? "text-white" : "text-ink-soft"
                  }`}
                >
                  {iv === "month" ? t("pricing.monthly") : t("pricing.yearly")}
                </span>
              </button>
            ))}
          </div>
          {yearSaving > 0 && (
            <span className="badge bg-teal-dark">
              {t("pricing.save", { percent: yearSaving })}
            </span>
          )}
        </div>
      )}

      {errorTag && (
        <p
          role="alert"
          className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {KNOWN_TAGS.includes(errorTag)
            ? t(`errors.${errorTag}`)
            : t("common:errors.generic")}
        </p>
      )}

      {plans.length === 0 && <p>{t("pricing.noPlans")}</p>}

      <div className="flex flex-wrap justify-center gap-6">
        {visiblePlans.map((plan) => (
          <div
            key={plan.code}
            data-testid={`plan-${plan.code}`}
            className={`card card-interactive flex w-full flex-col rounded-xl3 p-7 sm:w-80${
              plan.badge ? " border-teal shadow-lift sm:-translate-y-3" : ""
            }`}
          >
            {plan.badge && <span className="badge mb-2 w-fit">{plan.badge}</span>}
            <h2 className="font-display text-xl font-extrabold">{plan.name}</h2>
            <p className="mt-2 font-display text-4xl font-black">
              {formatAmount(plan.amount, plan.currency, lang)}
              <span className="text-sm font-normal text-ink-soft">
                {plan.interval === "month"
                  ? t("pricing.perMonth")
                  : t("pricing.perYear")}
              </span>
            </p>
            {plan.annualSavingPercent !== null && (
              <p className="mt-1 text-sm text-teal-dark">
                {t("pricing.save", { percent: plan.annualSavingPercent })}
              </p>
            )}
            <p className="mt-2 text-sm text-ink-soft">
              {plan.aiGenerationLimit === null
                ? t("pricing.unlimited")
                : t("pricing.generations", { limit: plan.aiGenerationLimit })}
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              {plan.includedSubjects === null
                ? t("pricing.allSubjects")
                : t("pricing.includedSubjects", {
                    count: plan.includedSubjects,
                  })}
            </p>
            <ul className="mt-4 flex-1 space-y-2.5 text-sm">
              {plan.features.map((feature, index) => (
                <li
                  key={index}
                  className="transition-transform duration-150 hover:translate-x-1"
                >
                  <span aria-hidden="true" className="mr-2 font-black text-teal-dark">
                    ✓
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
            {(() => {
              const isCurrent = currentPlan?.code === plan.code;
              // A downgrade already queued at Stripe: don't invite them to
              // trigger it again — mark this card scheduled instead.
              const isScheduled = pendingChange?.planCode === plan.code;
              // Direction is by amount, matching the server's own upgrade/
              // downgrade guards (minor units, so yearly > monthly of a tier).
              const kind: "upgrade" | "downgrade" | null =
                currentPlan && !isCurrent
                  ? plan.amount > currentPlan.amount
                    ? "upgrade"
                    : plan.amount < currentPlan.amount
                      ? "downgrade"
                      : null
                  : null;
              const cls = `mt-6 w-full ${plan.badge ? "btn-teal" : "btn-ghost"}`;

              // No Stripe Price exists for the free plan — Task 19's guard
              // would reject the checkout. It is what you already have when
              // you have nothing, so the card states that rather than selling.
              if (plan.tier === "free") {
                return (
                  <button
                    type="button"
                    data-testid={`cta-${plan.code}`}
                    disabled
                    className={cls}
                  >
                    {t("pricing.ctaFree")}
                  </button>
                );
              }

              if (isCurrent) {
                return (
                  <button
                    type="button"
                    data-testid={`cta-${plan.code}`}
                    disabled
                    className={cls}
                  >
                    {t("pricing.ctaCurrent")}
                  </button>
                );
              }

              if (isScheduled) {
                return (
                  <button
                    type="button"
                    data-testid={`cta-${plan.code}`}
                    disabled
                    className={cls}
                  >
                    {t("pricing.ctaScheduled")}
                  </button>
                );
              }

              if (kind) {
                return (
                  <button
                    type="button"
                    data-testid={`cta-${plan.code}`}
                    // Plan changes live on /account, which opens this plan's
                    // confirm step from ?plan=.
                    onClick={() => navigate(`/account?plan=${plan.code}`)}
                    className={cls}
                  >
                    {t(
                      kind === "upgrade"
                        ? "pricing.ctaUpgrade"
                        : "pricing.ctaDowngrade",
                    )}
                  </button>
                );
              }

              const n = addons[plan.code] ?? 0;
              const fmt = (a: number) => formatAmount(a, plan.currency, lang);
              const addonAmount =
                plan.addonPrices.find((p) => p.quantity === n)?.amount ?? 0;
              const total = plan.amount + addonAmount;
              const ultimate = plans.find(
                (p) => p.tier === "ultimate" && p.interval === plan.interval,
              );

              return (
                <>
                  {plan.addonPrices.length > 0 && (
                    <div className="mt-5 space-y-3 rounded-2xl bg-sand p-4 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-display font-extrabold">
                          {t("pricing.extraSubjects")}
                        </span>
                        <AddonStepper
                          testId={`addons-${plan.code}`}
                          value={n}
                          max={Math.max(...plan.addonPrices.map((p) => p.quantity))}
                          onChange={(v) => setAddons({ ...addons, [plan.code]: v })}
                          label={t("pricing.extraSubjects")}
                          decreaseLabel={t("addons.fewer")}
                          increaseLabel={t("addons.more")}
                        />
                      </div>
                      {n > 0 && (
                        <dl
                          data-testid={`addon-total-${plan.code}`}
                          className="space-y-1 border-t border-hairline pt-3"
                        >
                          <div className="flex justify-between text-ink-soft">
                            <dt>{plan.name}</dt>
                            <dd>{fmt(plan.amount)}</dd>
                          </div>
                          <div className="flex justify-between text-ink-soft">
                            <dt>{t("pricing.addonLine", { count: n })}</dt>
                            <dd>{fmt(addonAmount)}</dd>
                          </div>
                          <div className="flex justify-between font-display text-base font-extrabold">
                            <dt>{t("pricing.total")}</dt>
                            <dd>{fmt(total)}</dd>
                          </div>
                        </dl>
                      )}
                      {n > 0 && ultimate && total >= ultimate.amount && (
                        <p
                          data-testid={`ultimate-hint-${plan.code}`}
                          className="rounded-2xl bg-teal/10 px-3 py-2 font-semibold text-teal-dark"
                        >
                          {t("pricing.ultimateHint", { plan: ultimate.name })}
                        </p>
                      )}
                      <p className="text-xs text-ink-soft">{t("pricing.lockNotice")}</p>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`cta-${plan.code}`}
                    disabled={authLoading || pendingCode === plan.code}
                    onClick={() => void onChoosePlan(plan.code)}
                    className={cls}
                  >
                    {t("pricing.cta")}
                  </button>
                </>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Pricing;
